import { Box, Button, Drawer, IconButton, Tab, Tabs, Typography } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { HttpFunction, TimerFunction } from '../../../types/FunctionApp'
import { ExecutionResultDto } from '../../../types/ExecuteFunctionDto'
import RequestTab from './RequestTab'
import ResponseTab from './ResponseTab'

type SelectedFunction =
  | { type: 'http'; func: HttpFunction }
  | { type: 'timer'; func: TimerFunction }

interface ExecutionDrawerProps {
  open: boolean
  selectedFunc: SelectedFunction | null
  lastPayload: string | null
  lastContentType: string | null
  onClose: () => void
  onExecuteHttp: (routeParams: Record<string, string>, headers: Record<string, string>, body: string, contentType: string) => void
  onExecuteTimer: () => void
  isExecuting: boolean
  result: ExecutionResultDto | null
}

const DRAWER_WIDTH = 480

const ExecutionDrawer = ({ open, selectedFunc, lastPayload, lastContentType, onClose, onExecuteHttp, onExecuteTimer, isExecuting, result }: ExecutionDrawerProps) => {
  const { t } = useTranslation()
  const [tab, setTab] = useState(0)
  const [routeParams, setRouteParams] = useState<Record<string, string>>({})
  const [headers, setHeaders] = useState<{ key: string; value: string }[]>([])
  const [body, setBody] = useState('')
  const [contentType, setContentType] = useState('application/json')

  useEffect(() => {
    if (!open || !selectedFunc) return
    setBody(lastPayload ?? '')
    setContentType(lastContentType ?? 'application/json')
  }, [open, selectedFunc, lastPayload, lastContentType])

  const handleClose = () => {
    setTab(0)
    setRouteParams({})
    setHeaders([])
    setBody('')
    setContentType('application/json')
    onClose()
  }

  const handleExecute = () => {
    if (!selectedFunc) return

    if (selectedFunc.type === 'http') {
    const headersMap: Record<string, string> = {}
    headers.forEach((h) => {
      if (h.key.trim()) headersMap[h.key] = h.value
    })
      onExecuteHttp(routeParams, headersMap, body, contentType)
    } else {
      onExecuteTimer()
    }
    setTab(1)
  }

  if (!selectedFunc) return null

  const funcName = selectedFunc.func.name
  const subtitle =
    selectedFunc.type === 'http'
      ? `${selectedFunc.func.methods.map((m) => m.toUpperCase()).join(', ')} — ${selectedFunc.func.route}`
      : `Timer — ${selectedFunc.func.schedule}`

  return (
    <Drawer anchor="right" open={open} onClose={handleClose} slotProps={{ paper: { sx: { width: DRAWER_WIDTH } } }}>
      <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="h6" sx={{ fontSize: 16 }}>
            {funcName}
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ mb: 2 }}>
          {subtitle}
        </Typography>

        <Tabs value={tab} onChange={(_, v: number) => setTab(v)} sx={{ mb: 2 }}>
          <Tab label={t('command.request', 'Request')} />
          <Tab label={t('command.response', 'Response')} />
        </Tabs>

        <Box sx={{ flex: 1, overflow: 'auto' }}>
          {tab === 0 && selectedFunc.type === 'http' && (
            <RequestTab
              func={selectedFunc.func}
              routeParams={routeParams}
              onRouteParamChange={(name, value) => setRouteParams((prev) => ({ ...prev, [name]: value }))}
              headers={headers}
              onHeaderChange={(index, field, val) =>
                setHeaders((prev) => prev.map((h, i) => (i === index ? { ...h, [field]: val } : h)))
              }
              onAddHeader={() => setHeaders((prev) => [...prev, { key: '', value: '' }])}
              onRemoveHeader={(index) => setHeaders((prev) => prev.filter((_, i) => i !== index))}
              body={body}
              onBodyChange={setBody}
              contentType={contentType}
              onContentTypeChange={setContentType}
              onExecute={handleExecute}
              isExecuting={isExecuting}
            />
          )}
          {tab === 0 && selectedFunc.type === 'timer' && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, height: '100%' }}>
              <Typography color="text.secondary">
                {t('command.timerExecuteInfo', 'This will trigger the timer function immediately via the admin API.')}
              </Typography>
              <Button
                variant="contained"
                color="success"
                onClick={handleExecute}
                disabled={isExecuting}
                fullWidth
              >
                {isExecuting ? t('command.executing', 'Executing...') : t('command.execute', 'Execute')}
              </Button>
            </Box>
          )}
          {tab === 1 && <ResponseTab result={result ?? null} isExecuting={isExecuting} />}
        </Box>
      </Box>
    </Drawer>
  )
}

export default ExecutionDrawer
